'use client'

import { useState, useMemo, useRef, useEffect } from 'react'
import { useDashboardData } from '@/hooks/useDashboardData'
import { useFilterStore } from '@/stores/filterStore'
import { useBusinessUnitStore } from '@/stores/businessUnitStore'
import { useFilteredData } from '@/hooks/useFilters'
import { formatRelativeTime } from '@/lib/dates'
import { SLA_THRESHOLDS } from '@/lib/calculations'
import { Clock, AlertTriangle } from 'lucide-react'
import type { FilterCategory } from '@/types'

const CATEGORIES: { value: FilterCategory; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'MSO', label: 'MSO' },
  { value: 'PSO', label: 'PSO' },
  { value: 'Internal', label: 'Internal' },
]

const PERIODS = [7, 30, 90, 180]

export default function Header() {
  const { activeEngineers, activeProjectIds, updatedAt, tasks, timesheets, isLoading, engineerRoles, businessUnits, currentBuId } = useDashboardData()
  const setSelectedBuId = useBusinessUnitStore(s => s.setSelectedBuId)
  const { category, engineer, period, customDateFrom, customDateTo, setCategory, setEngineer, setPeriod, setCustomDateRange, clearCustomDateRange } = useFilterStore()
  const [alertOpen, setAlertOpen] = useState(false)
  const alertRef = useRef<HTMLDivElement>(null)

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (alertRef.current && !alertRef.current.contains(e.target as Node)) setAlertOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  // Action items
  const alerts = useMemo(() => {
    if (isLoading || !tasks.length) return []
    const items: { severity: 'red' | 'yellow' | 'orange'; text: string }[] = []
    const ft = tasks.filter(t => !t.cancelled && activeProjectIds.includes(t.projId))
    const now = new Date()

    // SLA breaches per engineer
    const breachByEng: Record<string, number> = {}
    ft.filter(t => t.age !== null && t.stage !== 'Done' && t.stage !== 'Backlog').forEach(t => {
      const sla = SLA_THRESHOLDS[t.cat] || 30
      if ((t.age || 0) > sla) {
        t.users.filter(u => activeEngineers.includes(u)).forEach(u => {
          breachByEng[u] = (breachByEng[u] || 0) + 1
        })
      }
    })
    Object.entries(breachByEng).sort((a, b) => b[1] - a[1]).forEach(([eng, count]) => {
      items.push({ severity: 'red', text: `${eng} has ${count} SLA breach${count > 1 ? 'es' : ''}` })
    })

    // Unassigned tasks >7 days
    const unassigned = ft.filter(t => t.users.length === 0 && t.stage !== 'Done' && t.stage !== 'Backlog' && (t.age || 0) > 7)
    if (unassigned.length > 0) items.push({ severity: 'yellow', text: `${unassigned.length} tasks unassigned for >7 days` })

    // Blocked >3 days
    const blocked = ft.filter(t => t.stage.toLowerCase().includes('block') && (t.time_in_status || 0) > 3)
    if (blocked.length > 0) items.push({ severity: 'orange', text: `${blocked.length} tasks blocked for >3 days` })

    // Timesheet gaps
    const twoWeeksStr = new Date(now.getTime() - 14 * 864e5).toISOString().slice(0, 10)
    activeEngineers.forEach(eng => {
      const hrs = timesheets.filter(ts => ts.user === eng && ts.date >= twoWeeksStr).reduce((a, ts) => a + ts.hours, 0)
      if (hrs / 10 < 5 && hrs > 0) items.push({ severity: 'orange', text: `${eng} averaging ${(hrs / 10).toFixed(1)}h/day (2 weeks)` })
    })

    return items
  }, [tasks, timesheets, activeEngineers, activeProjectIds, isLoading])

  // Date range state for picker
  const dateFrom = customDateFrom ? new Date(customDateFrom) : null
  const dateTo = customDateTo ? new Date(customDateTo) : null

  if (isLoading) return <div className="h-14 border-b border-slate-200 dark:border-slate-800" />

  const isIndividual = engineer !== 'all'
  const severityColors = { red: 'text-red-500 border-red-300 dark:border-red-800', yellow: 'text-amber-500 border-amber-300 dark:border-amber-800', orange: 'text-orange-500 border-orange-300 dark:border-orange-800' }

  return (
    <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-sm border-b border-slate-200 dark:bg-slate-900/80 dark:border-slate-800">
      <div className="flex items-center gap-3 px-6 h-14">
        {/* Business Unit */}
        {businessUnits.length > 1 && (
          <>
            <select value={currentBuId ?? ''} onChange={e => setSelectedBuId(parseInt(e.target.value))}
              className="text-sm bg-transparent border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 px-2 py-1.5 font-medium focus:outline-none focus:border-blue-500">
              {businessUnits.map(bu => <option key={bu.id} value={bu.id}>{bu.name}</option>)}
            </select>
            <div className="w-px h-5 bg-slate-200 dark:bg-slate-700" />
          </>
        )}
        {businessUnits.length === 1 && (
          <>
            <span className="text-sm font-medium text-slate-600 dark:text-slate-300 px-2">{businessUnits[0].name}</span>
            <div className="w-px h-5 bg-slate-200 dark:bg-slate-700" />
          </>
        )}

        {/* Category */}
        <div className="flex items-center gap-0.5">
          {CATEGORIES.map(c => (
            <button key={c.value} onClick={() => setCategory(c.value)}
              className={`px-2.5 py-1 text-sm font-medium transition-colors ${category === c.value ? 'bg-blue-600 text-white' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100 dark:hover:text-slate-200 dark:hover:bg-slate-800'}`}>
              {c.label}
            </button>
          ))}
        </div>
        <div className="w-px h-5 bg-slate-200 dark:bg-slate-700" />

        {/* Engineer */}
        <select value={engineer} onChange={e => setEngineer(e.target.value)}
          className="text-sm bg-transparent border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 px-2 py-1.5 focus:outline-none focus:border-blue-500">
          <option value="all">All Engineers ({activeEngineers.length})</option>
          {activeEngineers.map(e => <option key={e} value={e}>{e}</option>)}
        </select>

        {isIndividual && (
          <span className="text-xs text-slate-400 uppercase">{engineerRoles[engineer] || 'L1'}</span>
        )}

        <div className="w-px h-5 bg-slate-200 dark:bg-slate-700" />

        {/* Period */}
        <div className="flex items-center gap-0.5">
          {PERIODS.map(p => (
            <button key={p} onClick={() => { setPeriod(p); clearCustomDateRange() }}
              className={`px-2 py-1 text-sm font-medium transition-colors ${period === p && !customDateFrom ? 'bg-blue-600 text-white' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100 dark:hover:text-slate-200 dark:hover:bg-slate-800'}`}>
              {p}d
            </button>
          ))}
        </div>

        {/* Date range */}
        <div className="flex items-center gap-1.5">
          <input type="date" value={customDateFrom || ''} max={new Date().toISOString().slice(0,10)}
            onChange={e => {
              const from = e.target.value
              if (!from) return
              const to = customDateTo || new Date().toISOString().slice(0,10)
              setCustomDateRange(from, to)
            }}
            className="w-[130px] px-2 py-1 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 focus:outline-none focus:border-blue-500" />
          <span className="text-sm text-slate-400">to</span>
          <input type="date" value={customDateTo || ''} max={new Date().toISOString().slice(0,10)}
            onChange={e => {
              const to = e.target.value
              if (!to) return
              const from = customDateFrom || '2026-01-01'
              setCustomDateRange(from, to)
            }}
            className="w-[130px] px-2 py-1 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 focus:outline-none focus:border-blue-500" />
          {customDateFrom && (
            <button onClick={clearCustomDateRange} className="text-sm text-slate-400 hover:text-red-500 px-1">&times;</button>
          )}
        </div>

        <div className="flex-1" />

        {/* Action Items */}
        {alerts.length > 0 && (
          <div className="relative" ref={alertRef}>
            <button onClick={() => setAlertOpen(!alertOpen)}
              className={`flex items-center gap-1.5 px-2.5 py-1 text-xs border transition-colors ${alerts.some(a => a.severity === 'red') ? severityColors.red : severityColors.yellow}`}>
              <AlertTriangle size={13} />
              {alerts.length}
            </button>
            {alertOpen && (
              <div className="absolute right-0 top-full mt-1 w-80 max-h-72 overflow-y-auto bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-lg z-50">
                <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-400 border-b border-slate-200 dark:border-slate-800">Action Items</div>
                {alerts.map((a, i) => (
                  <div key={i} className="flex items-start gap-2 px-3 py-2 text-xs border-b border-slate-100 dark:border-slate-800/50">
                    <span className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${a.severity === 'red' ? 'bg-red-500' : a.severity === 'yellow' ? 'bg-amber-500' : 'bg-orange-500'}`} />
                    <span className="text-slate-600 dark:text-slate-300">{a.text}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Sync time */}
        {updatedAt && (
          <div className="flex items-center gap-1.5 text-xs text-slate-400">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            <Clock size={11} />
            <span>Last synced {formatRelativeTime(updatedAt)}</span>
          </div>
        )}
      </div>
    </header>
  )
}
